import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, CreditCard, Trash2, ArrowRight, CheckCircle, Loader2, PrinterIcon } from 'lucide-react';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import productService from '../services/productService';
import vendaService, { VendaItemDTO } from '../services/vendaService';
import { ProductResponseDTO, ProductPriceResponseDTO } from '../types/product';
import { authService } from '../services/authService';
import paymentService from '../services/paymentService';
import clientService from '../services/clientService';
import { paymentTypeService } from '../services/paymentTypeService';
import { PaymentType, PaymentResponseDTO } from '../types/payment';
import caixaService from '../services/caixaService';
import { useNavigate } from 'react-router-dom';
import { listCaixas, getCaixaUltimoAberto } from '../services/caixasService';
import { formatFromRaw, parseRawToNumber, rawFromNumber, getNextRawOnKey, rawFromTyped } from '../utils/decimalMask';
import alertService from '../services/alertService';
import PrintService from 'services/printService';

type Step = 'itens' | 'pagamento' | 'concluida';

const PDVPage: React.FC = () => {
  const navigate = useNavigate();
  const companyName = authService.getCompanyName() || 'Comercial';
  const [step, setStep] = useState<Step>('itens');
  const [productInput, setProductInput] = useState('');
  const [suggestions, setSuggestions] = useState<ProductResponseDTO[]>([]);
  const [suggestIndex, setSuggestIndex] = useState<number>(-1);
  const [selectedProduct, setSelectedProduct] = useState<ProductResponseDTO | null>(null);
  const [currentPrice, setCurrentPrice] = useState<ProductPriceResponseDTO | null>(null);
  const [currentStockQty, setCurrentStockQty] = useState<number>(0);
  const [itemQuantity, setItemQuantity] = useState<number>(1);
  const [itemPrice, setItemPrice] = useState<number>(0);
  const [items, setItems] = useState<VendaItemDTO[]>([]);
  const [observacoes, setObservacoes] = useState<string>('');
  const [vendaId, setVendaId] = useState<string | null>(null);
  const [types, setTypes] = useState<PaymentType[]>([]);
  const [payments, setPayments] = useState<PaymentResponseDTO[]>([]);
  // Linhas por tipo de pagamento (id do tipo)
  const [paymentRows, setPaymentRows] = useState<Record<string, { valor: string; pendingId?: string }>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const productInputRef = useRef<HTMLDivElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  const printYesRef = useRef<HTMLButtonElement>(null);
  const clientSelectRef = useRef<HTMLDivElement>(null);
  const pdvRootRef = useRef<HTMLDivElement>(null);
  const qtyInputRefsDesktop = useRef<HTMLInputElement[]>([]);
  const priceInputRefsDesktop = useRef<HTMLInputElement[]>([]);
  const qtyInputRefsMobile = useRef<HTMLInputElement[]>([]);
  const priceInputRefsMobile = useRef<HTMLInputElement[]>([]);
  const [selectLoading, setSelectLoading] = useState<boolean>(false);
  const [caixaAberto, setCaixaAberto] = useState<boolean | null>(null);
  const [searchResetSeq, setSearchResetSeq] = useState<number>(0);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showPrintConfirm, setShowPrintConfirm] = useState<boolean>(false);
  const [showSavingModal, setShowSavingModal] = useState<boolean>(false);
  const [saleStatus, setSaleStatus] = useState<string | null>(null);
  const [showChangeClientModal, setShowChangeClientModal] = useState<boolean>(false);
  const [unsavedClientId, setUnsavedClientId] = useState<string | null>(null);
  const [unsavedClientName, setUnsavedClientName] = useState<string | null>('COMSUMIDOR FINAL');
  const [clientOptions, setClientOptions] = useState<Array<{ id: string; nome?: string; descricao?: string }>>([]);
  const [clientSelectedId, setClientSelectedId] = useState<string>('');
  const [clientSearchLoading, setClientSearchLoading] = useState<boolean>(false);
  const clientSearchTimeoutRef = useRef<number | null>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);

  useEffect(() => {
    const onResize = () => setIsMobileOrTablet(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Aguarda próximo tick para garantir que estados tenham sido aplicados
  const nextTick = () => new Promise<void>(resolve => setTimeout(resolve, 0));

  const total = useMemo(() => items.reduce((s, i) => s + i.valorTotal, 0), [items]);
  const isPaidStatus = (status: any) => ['EFETUADO', 'PAGO'].includes(String(status).toUpperCase());
  const isPendingStatus = (status: any) => String(status).toUpperCase() === 'PENDENTE';
  const totalPaid = useMemo(() => payments.filter(p => isPaidStatus(p.status)).reduce((s, p) => s + (p.valor || 0), 0), [payments]);
  const restante = useMemo(() => Math.max(0, total - totalPaid), [total, totalPaid]);
  const hasPending = useMemo(() => payments.some(p => isPendingStatus(p.status)), [payments]);

  const getTypeLabel = (t: PaymentType) => t.nome || t.id;

  const sortedTypes = useMemo(() => {
    return [...types].sort((a, b) => {
      const ao = (a.ordem ?? 999999);
      const bo = (b.ordem ?? 999999);
      if (ao !== bo) return ao - bo;
      const an = String(getTypeLabel(a) || '');
      const bn = String(getTypeLabel(b) || '');
      return an.localeCompare(bn);
    });
  }, [types]);

  // Máscara para valores dos pagamentos (2 casas decimais)
  const [valorRawMap, setValorRawMap] = useState<Record<string, string>>({});
  const VALOR_DECIMALS = 2;
  const enteredTotal = useMemo(() => {
    let s = 0;
    for (const t of types) {
      const raw = valorRawMap[t.id];
      if (!raw) continue;
      const num = parseRawToNumber(raw, VALOR_DECIMALS);
      if (!isNaN(num)) s += num;
    }
    return +s.toFixed(2);
  }, [valorRawMap, types]);

  useEffect(() => {
    setError('');
    setSuccess('');
  }, [step]);

  useEffect(() => {
    const onEscClient = (e: KeyboardEvent) => {
      if ((e.key || '') === 'Escape' && showChangeClientModal) {
        e.preventDefault();
        setShowChangeClientModal(false);
      }
    };
    window.addEventListener('keydown', onEscClient);
    if (showChangeClientModal) {
      const t = setTimeout(() => {
        const focusable = clientSelectRef.current?.querySelector('[tabindex="0"]') as HTMLElement | null;
        focusable?.focus();
      }, 0);
      return () => {
        window.removeEventListener('keydown', onEscClient);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener('keydown', onEscClient);
  }, [showChangeClientModal]);

  useEffect(() => {
    const root = pdvRootRef.current;
    const anyModal = showPrintConfirm || showPaymentModal || showSavingModal || showChangeClientModal;
    if (root) {
      if (anyModal) {
        root.setAttribute('aria-hidden', 'true');
        root.setAttribute('inert', '');
        const ae = document.activeElement as HTMLElement | null;
        ae?.blur();
      } else {
        root.removeAttribute('aria-hidden');
        root.removeAttribute('inert');
      }
    }
    if (showPrintConfirm) {
      setTimeout(() => { printYesRef.current?.focus(); }, 0);
    }
  }, [showPrintConfirm, showPaymentModal, showSavingModal, showChangeClientModal]);

  // Focar automaticamente o campo de pesquisa ao carregar/entrar na etapa de itens
  useEffect(() => {
    if (step !== 'itens') return;
    const t = setTimeout(() => {
      const container = productInputRef.current;
      const focusable = container?.querySelector('[tabindex="0"]') as HTMLElement | null;
      focusable?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => {
    const loadCaixaStatus = async () => {
      try {
        // Descobrir caixa atual (aberto)
        const caixa = await getCaixaUltimoAberto();
        if(caixa && caixa.status === "ABERTO") {
          setCaixaAberto(true);
        } else {
          alertService.loading("Não ha caixa aberto. Contacte com o(a) administrador(a) do sistema!");
        }
      } catch {
        setCaixaAberto(null);
        alertService.loading("Não ha caixa aberto. Contacte com o(a) administrador(a) do sistema!");
      }
    };
    loadCaixaStatus();
  }, []);

  // Carregar automaticamente a última venda PENDENTE do vendedor ao abrir o PDV
  useEffect(() => {
    const loadPendingSale = async () => {
      try {
        const rawVendedorId = authService.getLoginData()?.usuarioId || authService.getUser()?.id;
        if (!rawVendedorId) return;
        const res = await vendaService.obterUltimaPendentePorVendedor(String(rawVendedorId));
        const sale: any = res?.data;
        if (sale?.id) {
          const idStr = String(sale.id);
          setVendaId(idStr);
          setSaleStatus(String(sale.status || 'PENDENTE'));
          const mappedItems: VendaItemDTO[] = (sale.itens || []).map((it: any) => ({
            produtoId: String(it.produtoId ?? it.produto?.id ?? ''),
            produtoNome: it.produtoNome ?? it.produto?.nome,
            produtoCodigoBarras: it.produtoCodigoBarras ?? it.produto?.codigoBarras,
            unidade: it.unidade || 'UN',
            quantidade: Number(it.quantidade || 0),
            precoUnitario: Number(it.precoUnitario ?? it.preco ?? 0),
            valorTotal: +(Number(it.valorTotal ?? (Number(it.quantidade || 0) * Number(it.precoUnitario ?? it.preco ?? 0)))).toFixed(2),
          }));
          setItems(mappedItems);
          setObservacoes(sale.observacoes || '');
          setQtyRawMap({});
          setPriceRawMap({});
          try {
            const payRes = await paymentService.listBySalePath(String(idStr));
            const pagos = ((payRes.data as any)?.content || (payRes.data as any) || []) as PaymentResponseDTO[];
            setPayments(pagos);
          } catch {}
          setSuccess('Venda PENDENTE carregada para continuar.');
        }
      } catch (e: any) {
        console.warn('Falha ao carregar venda pendente', getApiErrorMessage(e));
      }
    };
    loadPendingSale();
  }, []);

  // Atalho de teclado: Ctrl+P para avançar ao pagamento na etapa de itens
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const key = (e.key || '').toLowerCase();
      if (e.ctrlKey && key === 'p' && step === 'itens') {
        e.preventDefault();
        if (items.length > 0 && !loading) {
          goToPayment();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, items.length, loading]);

  // Foco rápido no campo de pesquisa de produto com F6
  useEffect(() => {
    const onF6 = (e: KeyboardEvent) => {
      if (e.key === 'F6' && step === 'itens') {
        e.preventDefault();
        // Focar o seletor e abrir a busca
        const container = productInputRef.current;
        const focusable = container?.querySelector('[tabindex="0"]') as HTMLElement | null;
        focusable?.focus();
      }
    };
    window.addEventListener('keydown', onF6);
    return () => window.removeEventListener('keydown', onF6);
  }, [step]);

  // Atalho F2: abrir modal de alteração de cliente
  useEffect(() => {
    const onF2 = (e: KeyboardEvent) => {
      if ((e.key || '') === 'F2' && step === 'itens' && !showChangeClientModal) {
        e.preventDefault();
        openChangeClientModal();
      }
    };
    window.addEventListener('keydown', onF2);
    return () => window.removeEventListener('keydown', onF2);
  }, [step, showChangeClientModal]);

  // Atalho de teclado: Esc para fechar o modal de pagamento
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if ((e.key || '') === 'Escape' && showPaymentModal) {
        e.preventDefault();
        setShowPaymentModal(false);
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [showPaymentModal]);

  // Atalho Esc para o modal de impressão de cupom e foco no "Sim"
  useEffect(() => {
    const onEscPrint = (e: KeyboardEvent) => {
      if ((e.key || '') === 'Escape' && showPrintConfirm) {
        e.preventDefault();
        setShowPrintConfirm(false);
        setShowPaymentModal(false);
        try { window.location.replace('/pdv'); } catch { /* noop */ }
      }
    };
    window.addEventListener('keydown', onEscPrint);
    // Garantir foco no botão "Sim" quando o modal abrir
    if (showPrintConfirm) {
      const t = setTimeout(() => { printYesRef.current?.focus(); }, 0);
      return () => {
        window.removeEventListener('keydown', onEscPrint);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener('keydown', onEscPrint);
  }, [showPrintConfirm]);

  // Bloquear scroll do body enquanto o modal estiver aberto
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (showPaymentModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [showPaymentModal]);

  const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  // Máscaras baseadas no util reutilizável
  const [qtyRawMap, setQtyRawMap] = useState<Record<number, string>>({});
  const [qtyReplaceOnTypeMap, setQtyReplaceOnTypeMap] = useState<Record<number, boolean>>({});
  const handleQtyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const refs = (isMobileOrTablet ? qtyInputRefsMobile.current : qtyInputRefsDesktop.current);
      const nextIdx = e.key === 'ArrowDown' ? Math.min(idx + 1, items.length - 1) : Math.max(idx - 1, 0);
      if (nextIdx !== idx) {
        setQtyReplaceOnTypeMap(prev => ({ ...prev, [nextIdx]: true }));
        const target = refs[nextIdx];
        if (target) {
          target.focus();
        }
      }
      return;
    }
    const replace = qtyReplaceOnTypeMap[idx] === true;
    const currentRaw = replace ? '0' : (qtyRawMap[idx] ?? rawFromNumber(items[idx]?.quantidade || 0, 3));
    const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
    if (!handled) return;
    e.preventDefault();
    setQtyReplaceOnTypeMap(prev => ({ ...prev, [idx]: false }));
    setQtyRawMap(prev => ({ ...prev, [idx]: nextRaw }));
    updateItemAt(idx, { quantidade: parseRawToNumber(nextRaw, 3) });
  };
  const [priceRawMap, setPriceRawMap] = useState<Record<number, string>>({});
  const [priceReplaceOnTypeMap, setPriceReplaceOnTypeMap] = useState<Record<number, boolean>>({});
  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const refs = (isMobileOrTablet ? priceInputRefsMobile.current : priceInputRefsDesktop.current);
      const nextIdx = e.key === 'ArrowDown' ? Math.min(idx + 1, items.length - 1) : Math.max(idx - 1, 0);
      if (nextIdx !== idx) {
        setPriceReplaceOnTypeMap(prev => ({ ...prev, [nextIdx]: true }));
        const target = refs[nextIdx];
        if (target) {
          target.focus();
        }
      }
      return;
    }
    const replace = priceReplaceOnTypeMap[idx] === true;
    const currentRaw = replace ? '0' : (priceRawMap[idx] ?? rawFromNumber(items[idx]?.precoUnitario || 0, 2));
    const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
    if (!handled) return;
    e.preventDefault();
    setPriceReplaceOnTypeMap(prev => ({ ...prev, [idx]: false }));
    setPriceRawMap(prev => ({ ...prev, [idx]: nextRaw }));
    updateItemAt(idx, { precoUnitario: parseRawToNumber(nextRaw, 2) });
  };


  // Resolve produto por código de barras, código ou descrição e sugere resultados
  const resolveProduct = async (query: string) => {
    const s = (query || '').trim();
    if (!s) { setSuggestions([]); return; }
    setLoading(true);
    setError('');
    try {
      const isNumeric = /^\d+$/.test(s);

      if (isNumeric) {
        // Somente código de barras quando termo for só números
        try {
          const byBarcode = await productService.getByCodigoBarras(s);
          const produto: ProductResponseDTO | null = byBarcode?.data || null;
          if (produto) {
            const details = await loadDetailsForProduct(produto);
            if (!details.priceOk) { return; }
            addItemWithDetails(produto, 1, details.precoVenda, details.estoqueQty, details.unidadeSigla);
            return;
          }
          // Não encontrou por código de barras
          setSuggestions([]);
        } catch (e: any) {
          setSuggestions([]);
          setError(getApiErrorMessage(e) || 'Produto não encontrado pelo código de barras');
        } finally {
          setLoading(false);
        }
        return;
      }

      // Fluxo não-numérico: tentar código interno e descrição
      let byCode: ProductResponseDTO | null = null;
      try { const bc = await productService.getByCodigo(s); byCode = bc?.data || null; } catch {}

      // Usar a rota dedicada de pesquisa de produtos
      const res = await productService.searchByTerm(s, { page: 0, size: 10 });
      const list = res?.data?.content || [];
      const combined = [byCode, ...list].filter(Boolean) as ProductResponseDTO[];
      const unique: Record<string, ProductResponseDTO> = {};
      for (const p of combined) {
        if (!p || !p.id) continue; unique[String(p.id)] = p;
      }
      const uniqueArr = Object.values(unique);
      setSuggestions(uniqueArr);
      if (uniqueArr.length === 1) {
        const details = await loadDetailsForProduct(uniqueArr[0]);
        if (!details.priceOk) { return; }
        addItemWithDetails(uniqueArr[0], 1, details.precoVenda, details.estoqueQty, details.unidadeSigla);
        setSuggestions([]);
      }
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Falha ao buscar produto';
      setError(msg);
      alertService.error('Erro', msg);
    } finally {
      setLoading(false);
    }
  };

  // Busca opções para o SearchableSelect
  const onSearchProducts = async (term: string) => {
    const s = (term || '').trim();
    if (!s || s.length < 2) { setSuggestions([]); return; }
    setSelectLoading(true);
    setError('');
    try {
      const isNumeric = /^\d+$/.test(s);

      if (isNumeric) {
        // Somente consultar por código de barras e auto-adicionar
        try {
          const bb = await productService.getByCodigoBarras(s);
          const produto: ProductResponseDTO | null = bb?.data || null;
          if (produto) {
            const details = await loadDetailsForProduct(produto);
            if (!details.priceOk) { return; }
            addItemWithDetails(produto, 1, details.precoVenda, details.estoqueQty, details.unidadeSigla);
            setSuggestions([]);
          } else {
            setSuggestions([]);
          }
        } catch (e: any) {
          setSuggestions([]);
          setError(getApiErrorMessage(e) || 'Produto não encontrado pelo código de barras');
        } finally {
          setSelectLoading(false);
        }
        return;
      }

      // Não-numérico: combinar código interno + descrição
      let byCode: ProductResponseDTO | null = null;
      try { const bc = await productService.getByCodigo(s); byCode = bc?.data || null; } catch {}

      // Usar a rota dedicada de pesquisa de produtos
      const res = await productService.searchByTerm(s, { page: 0, size: 10 });
      const list = res?.data?.content || [];
      const combined = [byCode, ...list].filter(Boolean) as ProductResponseDTO[];
      const unique: Record<string, ProductResponseDTO> = {};
      for (const p of combined) { if (!p || !p.id) continue; unique[String(p.id)] = p; }
      const uniqueArr = Object.values(unique);
      setSuggestions(uniqueArr);
      if (uniqueArr.length === 1) {
        const details = await loadDetailsForProduct(uniqueArr[0]);
        if (!details.priceOk) { return; }
        addItemWithDetails(uniqueArr[0], 1, details.precoVenda, details.estoqueQty);
        setSuggestions([]);
      }
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Falha ao buscar produto';
      setError(msg);
      alertService.error('Erro', msg);
    } finally {
      setSelectLoading(false);
    }
  };

  const handleSelectProduct = async (p: ProductResponseDTO) => {
    const details = await loadDetailsForProduct(p);
    // Não adicionar item se a API de preço falhar
    if (!details.priceOk) return;
    // Adiciona item automaticamente com quantidade 1 e preço atual
    addItemWithDetails(p, 1, details.precoVenda, details.estoqueQty, details.unidadeSigla);
  };

  const onProductInputKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (suggestIndex >= 0 && suggestions[suggestIndex]) {
        await handleSelectProduct(suggestions[suggestIndex]);
      } else if (suggestions[0]) {
        await handleSelectProduct(suggestions[0]);
      } else {
        await resolveProduct(productInput);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggestIndex((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestIndex((i) => Math.max(-1, i - 1));
    }
  };

  // Campos de quantidade/valor removidos do topo; navegação via teclado não é mais necessária.

  // Campo de valor removido do topo; lógica de Enter para adicionar item foi substituída pela adição automática ao selecionar o produto.

  // Auto-complete com debounce enquanto digita
  useEffect(() => {
    const t = setTimeout(() => {
      if (!selectedProduct) {
        resolveProduct(productInput);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [productInput, selectedProduct]);

  const loadDetailsForProduct = async (produto: ProductResponseDTO) => {
    setLoading(true);
    setError('');
    try {
      const priceRes = await productService.getCurrentPrice(produto.id);
      const stockRes = await vendaService.consultarEstoque(produto.id);
      const precoVenda = priceRes.data?.precoVenda ?? 0;
      const sdAny: any = stockRes.data as any;
      const sd = sdAny?.data ?? sdAny;
      const estoqueQty = (sd?.quantidadeDisponivel ?? sd?.quantidadeAtual ?? sd?.quantidade ?? 0);
      const unidadeSigla: string | undefined = produto.embalagemSigla || undefined;

      setSelectedProduct(produto);
      setCurrentPrice(priceRes.data || null);
      setCurrentStockQty(estoqueQty);
      setItemPrice(precoVenda);
      setItemQuantity(1);
      return { precoVenda, estoqueQty, unidadeSigla, priceOk: true };
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Falha ao carregar preço/estoque';
      setError(msg);
      alertService.error('Erro', msg);
      return { precoVenda: 0, estoqueQty: 0, unidadeSigla: undefined, priceOk: false };
    } finally {
      setLoading(false);
    }
  };

  // Adiciona item diretamente usando detalhes calculados (evita corrida de estado)
  const addItemWithDetails = (produto: ProductResponseDTO, quantidade: number, precoUnitario: number, estoqueQty: number, unidadeSigla?: string) => {
    const DEC_QTD = 3;
    const qtd = Math.max(1, quantidade || 1);
    const preco = Math.max(0, precoUnitario || 0);

    const controla = produto.controlaEstoque ?? false;
    const permiteNegativo = produto.permiteVendaEstoqueNegativo ?? false;

    setItems((prev) => {
      const idx = prev.findIndex(it => String(it.produtoId) === String(produto.id));
      console.log(produto);
      console.log("estoqueQty: ", estoqueQty);
      
      console.log("controla: ", controla);
      console.log("permiteNegativo: ", permiteNegativo);
      if (idx >= 0) {
        const existing = prev[idx];
        let newQty = existing.quantidade + qtd;
        if (controla && newQty > (estoqueQty || 0)) {
          const available = +((Number(estoqueQty) || 0).toFixed(DEC_QTD));
          if (!permiteNegativo) {
            if (available <= 0) {
              setError('Estoque indisponível para o produto.');
              alertService.error('Estoque indisponível', 'Não há quantidade disponível');
              return prev;
            }
            alertService.warning('Quantidade ajustada', `Disponível: ${available}`);
            newQty = available;
          } else {
            alertService.warning('Estoque insuficiente', `Produto permite venda com estoque negativo • Disp.: ${available}`);
          }
        }
        const updated: VendaItemDTO = {
          ...existing,
          quantidade: newQty,
          // manter preço unitário já aplicado ao item
          valorTotal: +(newQty * existing.precoUnitario).toFixed(2),
        };
        const clone = [...prev];
        clone[idx] = updated;
        return clone;
      }

      // Item novo
      let effQtd = qtd;
      if (controla) {
        const available = +((Number(estoqueQty) || 0).toFixed(DEC_QTD));
        if (!permiteNegativo) {
          if (available <= 0) {
            setError('Estoque indisponível para o produto.');
            alertService.error('Estoque indisponível', 'Não há quantidade disponível');
            return prev;
          }
          if (effQtd > available) {
            alertService.warning('Quantidade ajustada', `Disponível: ${available}`);
            effQtd = available;
          }
        } else if (effQtd > available) {
          alertService.warning('Estoque insuficiente', `Produto permite venda com estoque negativo • Disp.: ${available}`);
        }
      }
      const valorTotal = +(effQtd * preco).toFixed(2);
      const novoItem: VendaItemDTO = {
        produtoId: produto.id,
        produtoNome: produto.nome,
        produtoCodigoBarras: produto.codigoBarras,
        unidade: unidadeSigla || 'UN',
        quantidade: effQtd,
        precoUnitario: preco,
        valorTotal,
      };
      return [...prev, novoItem];
    });

    // limpar estados visuais
    setSelectedProduct(null);
    setCurrentPrice(null);
    setCurrentStockQty(0);
    setItemQuantity(1);
    setItemPrice(0);
    setProductInput('');
    setSuggestions([]);
    setSuggestIndex(-1);
    setSearchResetSeq((s) => s + 1);
    setTimeout(() => {
      const container = productInputRef.current;
      const focusable = container?.querySelector('[tabindex="0"]') as HTMLElement | null;
      focusable?.focus();
    }, 0);
  };

  const removeItem = (index: number) => {
    setItems(prev => {
      const next = prev.filter((_, i) => i !== index);
      const newQty: Record<number, string> = {};
      const newPrice: Record<number, string> = {};
      for (let i = 0; i < next.length; i++) {
        newQty[i] = rawFromNumber(Number(next[i]?.quantidade || 0), 3);
        newPrice[i] = rawFromNumber(Number(next[i]?.precoUnitario || 0), 2);
      }
      setQtyRawMap(newQty);
      setPriceRawMap(newPrice);
      setQtyReplaceOnTypeMap({});
      setPriceReplaceOnTypeMap({});
      return next;
    });
  };

  const updateItemAt = (index: number, updates: Partial<VendaItemDTO>) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== index) return it;
      const newQty = updates.quantidade !== undefined ? Math.max(0, Number(updates.quantidade) || 0) : it.quantidade;
      const newPrice = updates.precoUnitario !== undefined ? Math.max(0, Number(updates.precoUnitario) || 0) : it.precoUnitario;
      const newTotal = +(newQty * newPrice).toFixed(2);
      return { ...it, quantidade: newQty, precoUnitario: newPrice, valorTotal: newTotal };
    }));
  };

  const cancelarVendaPendente = async () => {
    if (!vendaId) return;
    try {
      setLoading(true);
      await vendaService.cancelar(String(vendaId));
      setVendaId(null);
      setSaleStatus(null);
      setItems([]);
      setObservacoes('');
      setPayments([]);
      setPaymentRows({});
      setValorRawMap({});
      setShowPaymentModal(false);
      setSuccess('Venda pendente cancelada. Você pode iniciar uma nova venda.');
      setError('');
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Falha ao cancelar venda pendente';
      setError(msg);
      alertService.error('Erro', msg);
    } finally {
      setLoading(false);
    }
  };

  const goToPayment = async () => {
    if (items.length === 0) {
      setError('Adicione ao menos um produto antes de continuar.');
      return;
    }
    setError('');
    setSuccess('Salvando venda...');
    setShowSavingModal(true);
    try {
      setLoading(true);

      // Garantir que a venda esteja criada como PENDENTE antes de abrir o modal
      let id = vendaId;
      if (!id) {
        const useDateTime = String(process.env.REACT_APP_VENDAS_DATE_TIME).toLowerCase() === 'true';
        const dataVenda = useDateTime ? new Date().toISOString() : new Date().toISOString().split('T')[0];
        const rawVendedorId = authService.getLoginData()?.usuarioId || authService.getUser()?.id;
        const normalizeId = (val: any) => {
          if (val === undefined || val === null) return val;
          const s = String(val);
          const numeric = Number(s);
          return Number.isFinite(numeric) && /^\d+$/.test(s) ? numeric : s;
        };
        const itensRequest = items.map(({ produtoId, quantidade, precoUnitario }) => ({
          produtoId: normalizeId(produtoId),
          quantidade: +((Number(quantidade) || 0).toFixed(3)),
          precoUnitario: +((Number(precoUnitario) || 0).toFixed(2)),
        }));
        const vendaReq: any = { dataVenda, itens: itensRequest, observacoes };
        if (rawVendedorId) {
          vendaReq.vendedorId = normalizeId(rawVendedorId);
        }
        if (unsavedClientId) vendaReq.clienteId = String(unsavedClientId);
        const criarRes = await vendaService.criar(vendaReq);
        const anyData: any = criarRes?.data;
        const newId: string | undefined = (anyData?.id ?? anyData?.data?.id);
        if (!newId) {
          setError('Falha ao criar venda: ID não retornado pela API.');
          setSuccess('');
          setShowSavingModal(false);
          setLoading(false);
          return;
        }
        id = newId;
        setVendaId(id);
        setSaleStatus('PENDENTE');
      }

      // Carregar tipos de pagamento disponíveis
      const typesRes = await paymentTypeService.list();
      const resolvedTypes = (typesRes.data || []).filter(t => t.ativo !== false);
      if (!resolvedTypes.length) {
        setTypes([]);
        setError('Nenhum tipo de pagamento disponível. Cadastre/ative tipos em Configurações.');
        setSuccess('');
        setShowSavingModal(false);
        setLoading(false);
        return;
      }
      setTypes(resolvedTypes);

      // Carregar pagamentos existentes da venda e preencher linhas locais
      const listRes = await paymentService.listBySalePath(String(id));
      const atualizados = ((listRes.data as any)?.content || (listRes.data as any) || []) as PaymentResponseDTO[];
      setPayments(atualizados);

      const initialRows: Record<string, { valor: string; pendingId?: string }> = {};
      const initialValorRaw: Record<string, string> = {};
      for (const t of resolvedTypes) {
        const pend = atualizados.find(p => isPendingStatus(p.status) && p.tipoPagamentoId === t.id);
        initialRows[t.id] = { valor: pend?.valor ? String(pend.valor).replace('.', ',') : '', pendingId: pend?.id };
        initialValorRaw[t.id] = rawFromNumber(Number(pend?.valor || 0), VALOR_DECIMALS);
      }
      setPaymentRows(initialRows);
      setValorRawMap(initialValorRaw);

      setSuccess('Venda salva como pendente. Abrindo pagamentos...');
      setShowSavingModal(false);
      await nextTick();
      setShowPaymentModal(true);
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Falha ao preparar pagamentos';
      setError(msg);
      setSuccess('');
      setShowSavingModal(false);
      alertService.error('Erro', msg);
    } finally {
      setLoading(false);
    }
  };

  // Salva as alterações nas linhas por tipo: cria/cancela pagamentos pendentes
  const savePaymentRows = async (): Promise<PaymentResponseDTO[]> => {
    if (!vendaId) {
      setError('Venda não identificada. Inicie a venda novamente.');
      return payments;
    }
    try {
      setLoading(true);
      const currentPending: Record<string, PaymentResponseDTO | undefined> = {};
      for (const p of payments) {
        if (isPendingStatus(p.status)) currentPending[p.tipoPagamentoId] = p;
      }
      for (const t of types) {
        const raw = valorRawMap[t.id] ?? '';
        const newValue = parseRawToNumber(raw || '', VALOR_DECIMALS) || 0;
        const existing = currentPending[t.id];

        if (existing) {
          const unchanged = (typeof existing.valor === 'number' ? existing.valor : 0) === (isNaN(newValue) ? 0 : newValue);

          if (isNaN(newValue) || newValue <= 0) {
            const cancelRes = await paymentService.cancel(existing.id, 'Removido pelo PDV');
            setPayments(prev => prev.map(p => (p.id === existing.id ? cancelRes.data : p)));
            continue;
          }

          if (!unchanged) {
            await paymentService.cancel(existing.id, 'Atualizado pelo PDV');
            const createRes = await paymentService.create({
              vendaId: vendaId,
              tipoPagamentoId: t.id,
              valor: newValue,
            });
            setPayments(prev => prev.map(p => (p.id === existing.id ? createRes.data : p)));
          }
        } else {
          if (!isNaN(newValue) && newValue > 0) {
            const createRes = await paymentService.create({
              vendaId: vendaId,
              tipoPagamentoId: t.id,
              valor: newValue,
            });
            setPayments(prev => [...prev, createRes.data]);
          }
        }
      }
      setSuccess('Alterações de pagamentos salvas.');
      // Atualiza e retorna a lista após salvar
      const listRes = await paymentService.listBySalePath(String(vendaId));
      const atualizados = ((listRes.data as any)?.content || (listRes.data as any) || []) as PaymentResponseDTO[];
      setPayments(atualizados);
      return atualizados;
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Erro ao salvar pagamentos';
      setError(msg);
      alertService.error('Erro', msg);
      return payments;
    } finally {
      setLoading(false);
    }
  };

  const confirmPaymentsAndFinalize = async () => {
    setLoading(true);
    alertService.loadingTime('Venda sendo registranda, aguarde...');
    setError('');
    try {
      // Garantir que exista uma venda criada; se não houver, criar agora
      let id = vendaId;
      if (!id) {
        const useDateTime = String(process.env.REACT_APP_VENDAS_DATE_TIME).toLowerCase() === 'true';
        const dataVenda = useDateTime ? new Date().toISOString() : new Date().toISOString().split('T')[0];
        const rawVendedorId = authService.getLoginData()?.usuarioId || authService.getUser()?.id;
        const normalizeId = (val: any) => {
          if (val === undefined || val === null) return val;
          const s = String(val);
          const numeric = Number(s);
          return Number.isFinite(numeric) && /^\d+$/.test(s) ? numeric : s;
        };
        const itensRequest = items.map(({ produtoId, quantidade, precoUnitario }) => ({
          produtoId: normalizeId(produtoId),
          quantidade: Math.trunc(Number(quantidade) || 0),
          precoUnitario: Number(precoUnitario) || 0,
        }));
        const vendaReq: any = { dataVenda, itens: itensRequest, observacoes };
        if (rawVendedorId) {
          vendaReq.vendedorId = normalizeId(rawVendedorId);
        }
        if (unsavedClientId) vendaReq.clienteId = String(unsavedClientId);
        // Logar payload para depuração rápida (valores e tipos)
        console.debug('[PDV] Enviando vendaReq', vendaReq, {
          dataVendaType: typeof vendaReq.dataVenda,
          vendedorIdType: typeof vendaReq.vendedorId,
          itemTypes: vendaReq.itens.map((it: any) => ({
            produtoIdType: typeof it.produtoId,
            quantidadeType: typeof it.quantidade,
            precoUnitarioType: typeof it.precoUnitario,
          })),
        });
        const criarRes = await vendaService.criar(vendaReq);
        const anyData: any = criarRes?.data;
        const newId: string | undefined = (anyData?.id ?? anyData?.data?.id);
        if (!newId) {
          setError('Falha ao criar venda: ID não retornado pela API.');
          setLoading(false);
          return;
        }
        id = newId;
        setVendaId(id);
        setSaleStatus('PENDENTE');
      } else {
        // Atualizar venda existente com itens antes de confirmar pagamentos
        try {
          const useDateTime = String(process.env.REACT_APP_VENDAS_DATE_TIME).toLowerCase() === 'true';
          const dataVenda = useDateTime ? new Date().toISOString() : new Date().toISOString().split('T')[0];
          const rawVendedorId = authService.getLoginData()?.usuarioId || authService.getUser()?.id;
          const normalizeId = (val: any) => {
            if (val === undefined || val === null) return val;
            const s = String(val);
            const numeric = Number(s);
            return Number.isFinite(numeric) && /^\d+$/.test(s) ? numeric : s;
          };
          const itensRequest = items.map(({ produtoId, quantidade, precoUnitario }) => ({
            produtoId: normalizeId(produtoId),
            quantidade: +((Number(quantidade) || 0).toFixed(3)),
            precoUnitario: +((Number(precoUnitario) || 0).toFixed(2)),
          }));
          const vendaReq: any = { dataVenda, itens: itensRequest, observacoes };
          if (rawVendedorId) vendaReq.vendedorId = normalizeId(rawVendedorId);
          if (unsavedClientId) vendaReq.clienteId = String(unsavedClientId);
          await vendaService.atualizar(String(id), vendaReq);
        } catch (err) {
          console.warn('Falha ao atualizar venda antes de finalizar', err);
        }
      }

      // Salva alterações de lançamento/cancelamento com base nas linhas inseridas
      const aposSalvar = await savePaymentRows();

      // Confirma todos os pagamentos pendentes
      const pendentes = aposSalvar.filter(p => isPendingStatus(p.status));
      const confirmados: PaymentResponseDTO[] = [];
      for (const p of pendentes) {
        try {
          const r = await paymentService.efetuar(p.id);
          confirmados.push(r.data);
        } catch (err: any) {
          console.warn('Falha ao confirmar pagamento', p.id, err?.message);
        }
      }
      if (confirmados.length) {
        setPayments(prev => prev.map(px => confirmados.find(c => c.id === px.id) ?? px));
      }

      // Atualiza a lista para estado definitivo
      if (id) {
        const listRes = await paymentService.listBySalePath(String(id));
        const atualizados = ((listRes.data as any)?.content || (listRes.data as any) || []) as PaymentResponseDTO[];
        setPayments(atualizados);

        const totalPagoLocal = atualizados.filter(p => isPaidStatus(p.status)).reduce((s, p) => s + (p.valor || 0), 0);
        const restanteLocal = Math.max(0, total - totalPagoLocal);
        const temPendentesLocal = atualizados.some(p => isPendingStatus(p.status));

        if (restanteLocal > 0 || temPendentesLocal) {
          setError('Pagamentos ainda não quitados. Ajuste os valores e tente novamente.');
          setLoading(false);
          return;
        }

        // Diagnóstico antes de finalizar: coletar flags e estoque dos itens
        const diagnoseStockBeforeFinalize = async () => {
          const diags: Array<{ produtoId: string; produtoNome?: string; controlaEstoque: boolean; permiteNegativo: boolean; quantidadeSolicitada: number; estoqueDisponivel: number }> = [];
          try {
            for (const it of items) {
              try {
                const [prodRes, estRes] = await Promise.all([
                  productService.getById(String(it.produtoId)),
                  vendaService.consultarEstoque(String(it.produtoId)),
                ]);
                const prod = prodRes?.data;
                const sdAny: any = estRes.data as any;
                const sd = sdAny?.data ?? sdAny;
                const estoque = (sd?.quantidadeDisponivel ?? sd?.quantidadeAtual ?? sd?.quantidade ?? 0);
                diags.push({
                  produtoId: String(it.produtoId),
                  produtoNome: it.produtoNome || prod?.nome,
                  controlaEstoque: !!(prod?.controlaEstoque),
                  permiteNegativo: !!(prod?.permiteVendaEstoqueNegativo),
                  quantidadeSolicitada: it.quantidade,
                  estoqueDisponivel: estoque,
                });
              } catch (inner) {
                diags.push({
                  produtoId: String(it.produtoId),
                  produtoNome: it.produtoNome,
                  controlaEstoque: false,
                  permiteNegativo: false,
                  quantidadeSolicitada: it.quantidade,
                  estoqueDisponivel: 0,
                });
              }
            }
          } catch {}
          return diags;
        };

        try {
          await vendaService.finalizar(id);
        } catch (e: any) {
          const msg = getApiErrorMessage(e);
          const diags = await diagnoseStockBeforeFinalize();
          const insuf = /estoque insuficiente/i.test(String(msg || e?.message || ''));
          if (insuf) {
            const offenders = diags.filter(d => d.controlaEstoque && d.quantidadeSolicitada > d.estoqueDisponivel);
            if (offenders.length) {
              const allows = offenders.filter(d => d.permiteNegativo);
              if (allows.length) {
                const nomes = allows.map(a => a.produtoNome || a.produtoId).join(', ');
                const full = `Estoque insuficiente na finalização. Os produtos (${nomes}) estão configurados para permitir venda com estoque negativo. Verifique se a configuração está ativa no backend e sincronizada. Disponível: ${allows.map(a => a.estoqueDisponivel).join(', ')} • Necessário: ${allows.map(a => a.quantidadeSolicitada).join(', ')}`;
                setError(full);
                alertService.error('Erro', full);
              } else {
                const m = msg || 'Falha ao finalizar: estoque insuficiente.';
                setError(m);
                alertService.error('Erro', m);
              }
            } else {
              const m = msg || 'Falha ao finalizar: estoque insuficiente.';
              setError(m);
              alertService.error('Erro', m);
            }
          } else {
            const m = msg || 'Falha ao finalizar venda.';
            setError(m);
            alertService.error('Erro', m);
          }
          setLoading(false);
          return;
        }
      }

      setSuccess('');
      alertService.close();
      setShowPaymentModal(false);
      setShowPrintConfirm(true);
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Falha ao confirmar pagamentos/finalizar venda';
      setError(msg);
      alertService.error('Erro', msg);
    } finally {
      setLoading(false);
      // Em caso de erro, garantir que loading seja fechado
      alertService.close();
    }
  };

  // (Obsoleto) substituído por savePaymentRows unificado

  // Funções para alteração de cliente (devem estar dentro do componente)
  const ensureVendaCriada = async (): Promise<string | null> => {
    let id = vendaId;
    if (id) return id;
    try {
      const useDateTime = String(process.env.REACT_APP_VENDAS_DATE_TIME).toLowerCase() === 'true';
      const dataVenda = useDateTime ? new Date().toISOString() : new Date().toISOString().split('T')[0];
      const rawVendedorId = authService.getLoginData()?.usuarioId || authService.getUser()?.id;
      const normalizeId = (val: any) => {
        if (val === undefined || val === null) return val;
        const s = String(val);
        const numeric = Number(s);
        return Number.isFinite(numeric) && /^\d+$/.test(s) ? numeric : s;
      };
      const itensRequest = items.map(({ produtoId, quantidade, precoUnitario }) => ({
        produtoId: normalizeId(produtoId),
        quantidade: +((Number(quantidade) || 0).toFixed(3)),
        precoUnitario: +((Number(precoUnitario) || 0).toFixed(2)),
      }));
      const vendaReq: any = { dataVenda, itens: itensRequest, observacoes };
      if (rawVendedorId) vendaReq.vendedorId = normalizeId(rawVendedorId);
      if (unsavedClientId) vendaReq.clienteId = String(unsavedClientId);
      const criarRes = await vendaService.criar(vendaReq);
      const anyData: any = criarRes?.data;
      const newId: string | undefined = (anyData?.id ?? anyData?.data?.id);
      if (!newId) { setError('Falha ao criar venda para atualizar cliente.'); return null; }
      setVendaId(newId);
      setSaleStatus('PENDENTE');
      return newId;
    } catch (e: any) {
      setError(getApiErrorMessage(e) || 'Falha ao criar venda.');
      return null;
    }
  };

  const openChangeClientModal = () => {
    setClientSelectedId(unsavedClientId || '');
    setClientOptions([]);
    setShowChangeClientModal(true);
  };

  const formatCpf = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 11);
    if (v.length <= 3) return v;
    if (v.length <= 6) return v.replace(/(\d{3})(\d+)/, '$1.$2');
    if (v.length <= 9) return v.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return v.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
  };

  const formatCnpj = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 14);
    if (v.length <= 2) return v;
    if (v.length <= 5) return v.replace(/(\d{2})(\d+)/, '$1.$2');
    if (v.length <= 8) return v.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
    if (v.length <= 12) return v.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
    return v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  };

  const formatDocumento = (doc?: string) => {
    const clean = (doc || '').replace(/[^\d]/g, '');
    if (!clean) return '';
    if (clean.length === 11) return formatCpf(clean);
    if (clean.length === 14) return formatCnpj(clean);
    return doc || '';
  };

  const onSearchClients = (term: string) => {
    if (clientSearchTimeoutRef.current) {
      window.clearTimeout(clientSearchTimeoutRef.current);
    }
    const timeoutId = window.setTimeout(async () => {
      const s = (term || '').trim();
      if (!s) { setClientOptions([]); return; }
      try {
        setClientSearchLoading(true);
        const res = await clientService.list({ page: 0, size: 10, sort: 'nome', direction: 'asc', filters: { nome: s, documento: s } });
        const list = res?.data?.content || [];
        const opts = list.map((c: any) => {
          const rawDoc = c?.cpf || c?.cnpj || c?.documento || '';
          return { id: String(c.id), nome: c.nome, descricao: formatDocumento(String(rawDoc)) };
        });
        setClientOptions(opts);
      } catch {
        setClientOptions([]);
      } finally {
        setClientSearchLoading(false);
      }
    }, 300);
    clientSearchTimeoutRef.current = timeoutId;
  };

  const applyClientToSale = async (id?: string) => {
    const cid = (id ? String(id) : '') || clientSelectedId || unsavedClientId || '';
    if (!cid) return;
    if (!vendaId) {
      const sel = clientOptions.find(o => String(o.id) === String(cid));
      setUnsavedClientId(String(cid));
      setUnsavedClientName(sel?.nome || null);
      alertService.loadingTime("", "", `${sel?.nome ? `${sel.nome}` : ''}. aplicado à venda...`);
      setTimeout(() => alertService.close(), 1200);
      setShowChangeClientModal(false);
      return;
    }
    try {
      setLoading(true);
      await vendaService.atualizarCliente(String(vendaId), String(cid));
      const sel = clientOptions.find(o => String(o.id) === String(cid));
      setUnsavedClientId(String(cid));
      setUnsavedClientName(sel?.nome || null);
      alertService.loadingTime(`Cliente atualizado${sel?.nome ? `: ${sel.nome}` : ''}.`);
      setTimeout(() => alertService.close(), 1000);
      setShowChangeClientModal(false);
    } catch (e: any) {
      const msg = getApiErrorMessage(e) || 'Falha ao atualizar cliente da venda';
      setError(msg);
      alertService.error('Erro', msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <div className="flex-1 flex flex-col" ref={pdvRootRef}>
        <header className="bg-red-600 text-white p-4 shadow-md">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-6 w-6" />
            <h1 className="text-sm md:text-xl font-semibold">PDV - {companyName} | </h1>
            <h4>{caixaAberto === true ? 'CAIXA ABERTO' : caixaAberto === false ? 'CAIXA FECHADO' : 'VERIFICANDO STATUS...'}</h4>
          </div>
        </header>
        <main className="flex-1 flex flex-col min-h-0 p-3 overflow-y-auto overflow-x-hidden text-base sm:text-lg md:text-xl">
          {step === 'itens' && (
            <div className="sticky top-0 z-30 bg-gray-100 border-b border-gray-200 mb-3">
              <div id="pdv-product-select" ref={productInputRef} className="w-full">
                <SearchableSelect
                  key={`product-search-${searchResetSeq}`}
                  options={suggestions.map(p => ({
                    id: p.id,
                    nome: p.nome,
                    descricao: `Código: ${p.codigo || ''} • Código de barras: ${p.codigoBarras || ''}`
                  }))}
                  value={selectedProduct?.id || ''}
                  onChange={async (val) => {
                    if (!val) {
                      setSelectedProduct(null);
                      setSuggestIndex(-1);
                      return;
                    }
                    const sel = suggestions.find(s => String(s.id) === String(val));
                    if (sel) {
                      await handleSelectProduct(sel);
                    } else {
                      try {
                        const res = await productService.getById(String(val));
                        if (res?.data) await handleSelectProduct(res.data);
                      } catch (e: any) {
                        const msg = getApiErrorMessage(e) || 'Falha ao selecionar produto';
                        setError(msg);
                        alertService.error('Erro', msg);
                      }
                    }
                  }}
                  placeholder="Digite para pesquisar..."
                  label="(F6) Produto (código, código de barras ou descrição)"
                  onSearch={onSearchProducts}
                  loading={selectLoading}
                  required
                  size={isMobileOrTablet ? 'lg' : 'xl'}
                />
              </div>
            </div>
          )}
          <div className="flex flex-col bg-white p-0 rounded-lg shadow-sm w-full h-full relative">
            {/* {error && ( <div className="p-3 rounded bg-red-50 text-red-700">{error}</div> )}
            {success && ( <div className="p-3 rounded bg-green-50 text-green-700">{success}</div> )} */}
            {/* Steps removidos conforme solicitação */}
            {step === 'itens' && (
              <>
                {/* Campo de busca movido para fora da área destacada */}
                {/* Total da venda movido para o canto inferior direito */}
                <div className="grid grid-cols-12 gap-6 w-full">
                  <div className="col-span-12 min-h-0">
                    <div className="rounded p-2 md:p-3 flex flex-col overflow-hidden">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12">
                          {/* Detalhes do produto removidos conforme solicitado */}
                          
                            <div className="flex items-center gap-3 mb-2">
                              <div className="font-semibold text-gray-800">Itens da venda</div>
                              <div className="inline-flex items-center gap-2 px-2 py-1 rounded bg-green-50 text-green-700 text-xs sm:text-sm">
                                {/* <span className="font-medium">Cliente:</span> */}
                                <span className="font-semibold truncate max-w-[50vw]">{unsavedClientName || 'COMSUMIDOR FINAL'}</span>
                              </div>
                            </div>
                            {items.length === 0 ? (
                              <div className="text-base text-gray-600">Nenhum item adicionado</div>
                            ) : (
                              <div className="flex-1 min-h-0">
                                <div className="rounded overflow-hidden w-full hidden lg:flex lg:flex-col lg:h-[52vh]">
                                  <div className="hidden lg:block">
                                    <table className="w-full text-sm sm:text-base table-fixed">
                                      <thead className="bg-gray-100 text-gray-600">
                                        <tr>
                                          <th className="py-1 px-3 w-12 text-left">ITEM</th>
                                          <th className="py-1 px-3 w-48 text-left">CÓD. BARRAS</th>
                                          <th className="py-1 px-3 text-left">DESCRIÇÃO</th>
                                          <th className="py-1 px-3 w-16 text-left">UN</th>
                                          <th className="py-1 px-3 w-24 text-right">QTDE</th>
                                          <th className="py-1 px-3 w-28 text-right">VR. UNIT</th>
                                          <th className="py-1 px-3 w-28 text-right">SUBTOTAL</th>
                                          <th className="py-1 px-3 w-12"></th>
                                        </tr>
                                      </thead>
                                    </table>
                                  </div>
                                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden hidden lg:block">
                                    <table className="w-full text-sm sm:text-base table-fixed">
                                      <tbody className="bg-yellow-50">
                                        {items.map((it, idx) => (
                                          <tr key={`${it.produtoId}-${idx}`} className="border-t border-yellow-200">
                                            <td className="px-3 text-left w-12">{idx + 1}</td>
                                            <td className="px-3 text-left w-48">{it.produtoCodigoBarras || '-'}</td>
                                            <td className="px-3 text-left break-words">{it.produtoNome || it.produtoId}</td>
                                            <td className="px-3 text-left w-16">{it.unidade || 'UN'}</td>
                                            <td className="px-3 text-right w-24">
                                              <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                className="w-24 border rounded px-2 text-right bg-yellow-50 border-yellow-300 text-sm sm:text-base h-8 font-semibold"
                                                value={formatFromRaw(qtyRawMap[idx] ?? rawFromNumber(it.quantidade, 3), 3)}
                                                onKeyDown={(e) => handleQtyKeyDown(e, idx)}
                                                onFocus={() => setQtyReplaceOnTypeMap(prev => ({ ...prev, [idx]: true }))}
                                                onChange={() => {}}
                                                ref={(el) => { if (el) qtyInputRefsDesktop.current[idx] = el; }}
                                              />
                                            </td>
                                            <td className="px-3 text-right w-28">
                                              <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                className="w-28 border rounded px-2 text-right bg-yellow-100 border-yellow-300 text-sm sm:text-base h-8 font-semibold"
                                                value={formatFromRaw(priceRawMap[idx] ?? rawFromNumber(it.precoUnitario, 2), 2)}
                                                onKeyDown={(e) => handlePriceKeyDown(e, idx)}
                                                onFocus={() => setPriceReplaceOnTypeMap(prev => ({ ...prev, [idx]: true }))}
                                                onChange={() => {}}
                                                ref={(el) => { if (el) priceInputRefsDesktop.current[idx] = el; }}
                                              />
                                            </td>
                                            <td className="px-3 text-right w-28">{formatCurrency(it.valorTotal)}</td>
                                            <td className="px-3 text-right w-12">
                                              <button onClick={() => removeItem(idx)} className="px-3 h-6 bg-yellow-200 rounded hover:bg-yellow-300 inline-flex items-center justify-center">
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                
                                {/* Cards apenas para tablets e mobile */}
                                <div className="lg:hidden space-y-3 w-full">
                                  {items.map((it, idx) => (
                                    <div key={`${it.produtoId}-${idx}`} className="bg-yellow-50 border border-yellow-200 rounded p-3 w-full">
                                      <div className="flex items-start justify-between">
                                        <div className="text-sm text-gray-500">#{idx + 1}</div>
                                        <button onClick={() => removeItem(idx)} className="px-3 h-10 bg-yellow-200 rounded hover:bg-yellow-300 inline-flex items-center justify-center">
                                          <Trash2 className="h-4 w-4 text-red-500" />
                                        </button>
                                      </div>
                                      <div className="mt-1 font-medium text-gray-800 break-words">{it.produtoNome || it.produtoId}</div>
                                      <div className="mt-1 text-xs text-gray-600">Cód. barras: {it.produtoCodigoBarras || '-'}</div>
                                      <div className="mt-2 flex items-center gap-2">
                                        <div className="flex-1">
                                          <label className="block text-xs text-gray-600">Qtde</label>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className="w-full border rounded px-2 py-2 text-right bg-yellow-100 border-yellow-300 text-sm h-10"
                                            value={formatFromRaw(qtyRawMap[idx] ?? rawFromNumber(it.quantidade, 3), 3)}
                                            onKeyDown={(e) => handleQtyKeyDown(e, idx)}
                                            onFocus={() => setQtyReplaceOnTypeMap(prev => ({ ...prev, [idx]: true }))}
                                            onChange={() => { /* controlado via onKeyDown */ }}
                                            ref={(el) => { if (el) qtyInputRefsMobile.current[idx] = el; }}
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <label className="block text-xs text-gray-600">Vr. Unit</label>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className="w-full border rounded px-2 py-2 text-right bg-yellow-100 border-yellow-300 text-sm h-10"
                                            value={formatFromRaw(priceRawMap[idx] ?? rawFromNumber(it.precoUnitario, 2), 2)}
                                            onKeyDown={(e) => handlePriceKeyDown(e, idx)}
                                            onFocus={() => setPriceReplaceOnTypeMap(prev => ({ ...prev, [idx]: true }))}
                                            onChange={() => { /* controlado via onKeyDown */ }}
                                            ref={(el) => { if (el) priceInputRefsMobile.current[idx] = el; }}
                                          />
                                        </div>
                                      </div>
                                      <div className="mt-2 flex items-center justify-between">
                                        <div className="text-xs text-gray-600">Un: {it.unidade || 'UN'}</div>
                                        <div className="text-base font-semibold">{formatCurrency(it.valorTotal)}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Total da venda removido desta área; card fixo será exibido abaixo */}

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Total da venda removido do card fixo; será exibido no footer */}

            {/* Modal de Pagamento */}
            {showPaymentModal && createPortal(
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
                <div role="dialog" aria-modal="true" className="bg-white max-w-[700px] max-h-[80vh] overflow-y-auto rounded-lg shadow-2xl ring-2 ring-red-600">
                  <div className="bg-red-500 px-4 py-2 flex items-center justify-between border-b-2 border-red-600">
                    <div className="text-white font-bold text-xl">VALOR TOTAL</div>
                    <div className="text-white font-extrabold text-3xl">{formatCurrency(total).replace(/^R\$\s?/, '')}</div>
                  </div>
                  <div className="p-2">
                    <table className="w-full">
                      <tbody>
                        {sortedTypes.map((t, i) => (
                          <tr key={t.id} className="border-b border-red-300">
                            <td className="px-2 py-2 w-8 text-red-700 font-bold">{i + 1}</td>
                            <td className="px-2 py-2 text-red-700">{getTypeLabel(t)}</td>
                            <td className="px-2 py-2 text-right">
                              <input
                                type="tel"
                                inputMode="numeric"
                                autoComplete="off"
                                className="w-40 border border-red-400 bg-white px-3 py-2 text-red-700 text-lg font-semibold text-right rounded focus:ring-2 focus:ring-red-500"
                                placeholder="0,00"
                                value={formatFromRaw(valorRawMap[t.id] ?? rawFromNumber(0, VALOR_DECIMALS), VALOR_DECIMALS)}
                                onKeyDown={(e) => {
                                  // Suporte a teclado físico (desktop)
                                  const currentRaw = valorRawMap[t.id] ?? rawFromNumber(0, VALOR_DECIMALS);
                                  const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
                                  if (handled) {
                                    e.preventDefault();
                                    setValorRawMap(prev => ({ ...prev, [t.id]: nextRaw }));
                                  }
                                }}
                                onInput={(e) => {
                                  // Suporte a teclado mobile (Android/iOS): atualiza a cada caractere
                                  const typed = (e.currentTarget.value || '');
                                  const normalized = rawFromTyped(typed, VALOR_DECIMALS);
                                  setValorRawMap(prev => ({ ...prev, [t.id]: normalized }));
                                }}
                                autoFocus={i === 0}
                              />
                            </td>
                          </tr>
                        ))}
                        {sortedTypes.length === 0 && (
                          <tr>
                            <td colSpan={3} className="text-center text-gray-500 px-3 py-6">Nenhum tipo disponível</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-gray-100 px-4 text-gray-800 py-2 flex items-center justify-between border-t-2 border-b-2 border-red-600">
                    <div className="text-red-800 font-normal text-lg w-full">VALOR TROCO</div>
                    <div className="text-red-800 font-normal text-lg">{formatCurrency(Math.max(enteredTotal - total, 0)).replace(/^R\$\s?/, '')}</div>
                  </div>
                  <div className="p-3 flex justify-end gap-2">
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      className="px-5 py-3 bg-gray-200 text-gray-700 rounded text-base w-full"
                      title="Esc"
                      disabled={loading}
                    >
                      Cancelar (Esc)
                    </button>
                    <button
                      onClick={async () => { await confirmPaymentsAndFinalize(); }}
                      className="px-5 py-3 bg-red-600 text-white rounded text-base text-full"
                      disabled={loading}
                    >
                      Finalizar
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Modal de Alteração de Cliente */}
            {showChangeClientModal && createPortal(
              <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60" role="dialog" aria-modal="true">
                <div className="bg-white rounded-lg shadow-lg w-[520px] max-w-[95vw]">
                  <div className="px-4 py-3 border-b">
                    <div className="text-lg font-semibold">Alterar Cliente da Venda</div>
                  </div>
                  <div className="px-4 py-4" ref={clientSelectRef}>
                    <SearchableSelect
                      options={clientOptions}
                      value={clientSelectedId}
                      onChange={async (v) => {
                        const id = String(v || '');
                        setClientSelectedId(id);
                        if (id) {
                          await applyClientToSale(id);
                        }
                      }}
                      placeholder="Digite para pesquisar..."
                      label="Cliente (nome ou CPF)"
                      onSearch={onSearchClients}
                      loading={clientSearchLoading}
                      size={isMobileOrTablet ? 'lg' : 'normal'}
                    />
                    {unsavedClientId && unsavedClientName && (
                      <div className="mt-2 p-2 rounded bg-blue-50 text-blue-700 text-sm">
                        Selecionado em tela: {unsavedClientName}
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 border-t flex justify-end gap-2">
                    <button onClick={() => setShowChangeClientModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded">Fechar</button>
                    <button onClick={() => applyClientToSale()} className="px-4 py-2 bg-green-600 text-white rounded" disabled={!clientSelectedId && !unsavedClientId}>Aplicar</button>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Modal de Salvamento */}
            {showSavingModal && (
              <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 transition-opacity">
                <div className="bg-white rounded-lg shadow-lg w-[420px] max-w-[90vw] p-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="h-6 w-6 animate-spin text-red-600" />
                    <div className="text-lg font-semibold text-gray-800">Salvando venda...</div>
                  </div>
                  <div className="mt-2 text-gray-600">Aguarde, abrindo o pagamento em seguida.</div>
                </div>
              </div>
            )}
            {showPrintConfirm && createPortal(
              <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60" role="dialog" aria-modal="true">
                <div className="bg-white rounded-lg shadow-lg w-[420px] max-w-[90vw]">
                  <div className="px-4 py-3 border-b bg-red-600 flex flex-row items-center gap-2 text-white">
                    <PrinterIcon />
                    <div className="text-lg text-white font-semibold">Impressão de Cupom</div>
                  </div>
                  <div className="flex flex-col text-center sm:text-left px-4 py-4 text-gray-700">
                    <span className="text-green-600 text-2xl">Venda registrada com sucesso.</span>
                    <span>Deseja gerar impressão do cupom agora?</span>
                  </div>
                  <div className="px-4 py-3 border-t flex justify-end gap-2">
                    <button
                      onClick={() => { setShowPrintConfirm(false); setShowPaymentModal(false); try { window.location.replace('/pdv'); } catch {} }}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                      title="Esc"
                    >
                      Não (Esc)
                    </button>
                    <button
                      ref={printYesRef}
                      autoFocus
                      onClick={() => { if (vendaId) navigate(`/pdv/cupom/${vendaId}?print=1`); }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-600 text-white rounded"
                    >
                      Sim
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {step === 'concluida' && (
              <div className="text-center py-16">
                <div className="inline-flex items-center gap-2 px-5 py-3 rounded bg-green-100 text-green-700 text-base">
                  <CheckCircle className="h-5 w-5" />
                  Venda finalizada com sucesso
                </div>
                <div className="mt-4 text-base text-gray-600">Você pode fechar esta janela ou iniciar uma nova venda.</div>
              </div>
            )}
          </div>
        </main>
        <footer className="bg-gray-50 border-t p-3 sticky bottom-0">
          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-2">
            {/* Botões à esquerda */}
            <div className="flex items-center gap-2 flex-1 w-full">
              <button onClick={openChangeClientModal} className="px-4 rounded-lg h-12 bg-gray-300 text-gray-900 text-base w-full sm:w-auto" title="F2">
                Cliente [F2]
              </button>
              {saleStatus === 'PENDENTE' && step === 'itens' && (
                <button onClick={cancelarVendaPendente} className="px-4 rounded-lg h-12 bg-red-600 text-white text-base w-full sm:w-auto" disabled={loading} >
                  Cancelar
                </button>
              )}
              {step === 'itens' && items.length > 0 && (
                <button onClick={goToPayment} className="px-4 rounded-lg h-12 bg-gray-300 text-gray-900 text-base w-full sm:w-auto" disabled={false} title="Ctrl+P">
                  Pagamento [Ctrl+P]
                </button>
              )}
              {step === 'pagamento' && (
                <>
                  <button
                    onClick={() => setStep('itens')}
                    className="px-4 h-12 bg-gray-200 text-red-700 rounded text-base w-full sm:w-auto"
                    disabled={loading}
                  >
                    Voltar aos Itens
                  </button>
                  <button
                    onClick={confirmPaymentsAndFinalize}
                    className="px-4 h-12 bg-green-600 text-white rounded text-base w-full sm:w-auto"
                    disabled={loading}
                  >
                    Finalizar
                  </button>
                </>
              )}
              {/* {error && ( <div className="p-3 rounded bg-red-50 text-red-700">{error}</div> )}
              {success && ( <div className="p-3 rounded bg-green-50 text-green-700">{success}</div> )} */}
            </div>

            {/* Total da venda à direita no footer */}
            <div className="sm:ml-auto flex flex-row sm:flex-col w-full sm:w-auto justify-center sm:justify-end">
              <div className="hidden sm:block text-xs text-gray-700 p-0 m-0">Total da venda</div>
              <div className="text-3xl sm:text-5xl font-bold text-red-700 p-0 m-0">{formatCurrency(total)}</div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default PDVPage;
  // Extrai mensagem amigável da resposta de erro da API
  const getApiErrorMessage = (err: any): string => {
    if (!err) return 'Erro inesperado.';
    const data = err?.response?.data;
    // Mensagem direta em string
    if (typeof data === 'string') return data;
    // Campos comuns de mensagem
    const primary = data?.message || data?.mensagem || data?.error || data?.title || err?.message;
    const detailsArray = Array.isArray(data?.errors) ? data.errors : undefined;
    const details = detailsArray?.length ? detailsArray[0] : (data?.detail || data?.detalhe);
    return [primary, details].filter(Boolean).join(' - ') || 'Falha ao processar requisição.';
  };